/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useEffect, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { ChevronArrowDownIcon } from '../../assets/icons/ChevronArrowDownIcon'
import { CloseIcon } from '../../assets/icons/CloseIcon'
import { BUTTON_STYLE_DEFAULT_PRIMARY } from '../../libraries/ButtonTheme'
import { TooltipPositionTypes } from '../../libraries/Tooltips'
import {
  getMergedClassNames,
  getUniqueKey,
  isMobile
} from '../../utils/HTMLutils'
import { languageValues } from '../../utils/LanguageUtils'
import { getStringWithoutSpecialChar } from '../../utils/MaskUtils'
import Button from '../button/Button'
import Checkbox from '../checkbox/Checkbox'
import IconButton from '../iconButton/IconButton'
import Input from '../input/Input'
import './Select.scss'
const rootClassName = 'component-select'

interface SelectProps {
  'tooltip-position'?: TooltipPositionTypes
  'tooltip-text'?: string
  className?: string
  onChange: (event?: any) => void
  value?: number | string | any[] | boolean
  label?: string
  placeholder?: string
  multiple?: boolean
  panelFilter?: boolean
  panelFilterPlaceholder?: string
  showPanelFooter?: boolean
  options?: {
    label?: string | number
    value?: number | string | boolean
  }[]
  noOptionMessage?: string
  multiplePlaceholder?: (number: any) => string
  clearButton?: boolean
  loading?: boolean
  disabled?: boolean
  preSelectUniqueOption?: boolean
  readOnly?: boolean
  tabIndex?: number
}

function Select(props: SelectProps) {
  const [id] = useState<string>(getUniqueKey())
  const [panelId] = useState<string>(getUniqueKey())
  const [panelOpened, setPanelOpened] = useState<boolean>(false)
  const [filterInput, setFilterInput] = useState<string>('')
  const [fakeSelectFocused, setFakeSelectFocused] = useState<boolean>(false)
  const [inter, setInter] = useState<any>(null)
  const interRef = useRef(inter)
  interRef.current = inter

  const getKey = (option: any) => {
    if (option) {
      if (option.key) return option.key
      if (option.value) return option.value
    }
    return getUniqueKey()
  }

  useEffect(() => {
    const func = (evt: any) => {
      if (evt.path) {
        if (
          document.querySelector(
            `#${panelId}.${rootClassName}-panel.panel-fade-enter-done`
          ) &&
          !evt.path.includes(
            document.querySelector(`#${id}.${rootClassName}-outer`)
          )
        )
          setPanelOpened(false)
      } else {
        // FIREFOX
        let aux = evt.target
        let deveFechar = true
        while (aux && aux.id !== 'root') {
          if (
            aux &&
            aux.id &&
            aux.id === id &&
            aux.classList.contains(`${rootClassName}-outer`)
          ) {
            deveFechar = false
            break
          }
          aux = aux.parentElement
        }
        if (deveFechar) {
          setPanelOpened(false)
        }
      }
    }
    const onEsc = (evt: any) => {
      if (evt.key === 'Escape') {
        setPanelOpened(false)
        evt.preventDefault()
      }
    }

    if (panelOpened) {
      document.addEventListener('keydown', onEsc)
      document.addEventListener('click', func)
      document.body.append(document.querySelector(`#${panelId}`) as HTMLElement)
      document
        ?.querySelector(`#${id}`)
        ?.append(document.querySelector(`#${panelId}`) as HTMLElement)
    } else {
      document.removeEventListener('keydown', onEsc)
      document.removeEventListener('click', func)
      setTimeout(() => setFilterInput(''), 400)
    }
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.removeEventListener('click', func)
    }
  }, [id, panelId, panelOpened])

  useEffect(() => {
    if (interRef.current) {
      clearInterval(interRef.current)
      setInter(null)
    }
    const position = () => {
      const trigger = document.querySelector(`#${id} input`) as HTMLElement
      const panel = document.getElementById(panelId) as HTMLElement

      if (!trigger || !panel) return
      const triggerBound = trigger.getBoundingClientRect()
      panel.style.width = `${triggerBound.width}px`

      const windowHeight = window.innerHeight
      const panelHeight = panel.getBoundingClientRect().height
      const fitBottom = panelHeight + triggerBound.bottom < windowHeight
      const fitTop = triggerBound.top - panelHeight > 0
      if (isMobile() || (!fitBottom && !fitTop)) {
        panel.style.top = 'unset'
        panel.style.bottom = '0'
        panel.style.left = '0'
        panel.style.width = '100%'
        panel.style.borderRadius = '8px 8px 0 0'
        panel.style.boxShadow = '0 -1px 2px 0 rgba(90, 100, 90, 0.3)'
      } else if (fitTop && !fitBottom) {
        panel.style.left = 'unset'
        panel.style.borderRadius = '8px'
        panel.style.top = `${triggerBound.top - panelHeight}px`
        panel.style.bottom = 'unset'
        panel.style.boxShadow = '0 0 2px 0 rgba(90, 100, 90, 0.3)'
      } else if (fitBottom) {
        panel.style.left = 'unset'
        panel.style.top = `${triggerBound.bottom + 2}px`
        panel.style.bottom = 'unset'
        panel.style.borderRadius = '0 0 8px 8px'
      } else {
        panel.style.top = 'unset'
        panel.style.bottom = '0'
        panel.style.left = '0'
        panel.style.width = '100%'
        panel.style.borderRadius = '8px 8px 0 0'
        panel.style.boxShadow = '0 -1px 2px 0 rgba(90, 100, 90, 0.3)'
      }
    }
    if (panelOpened) {
      position()
      setInter(setInterval(position, 100))
    }
    return () => {
      if (interRef.current) {
        clearInterval(interRef.current)
        setInter(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpened])

  const opcoes = JSON.stringify(props.options)
  useEffect(() => {
    if (
      props.preSelectUniqueOption &&
      props.options &&
      props.options.length === 1 &&
      ((!props.multiple &&
        (props.value === null ||
          props.value === undefined ||
          props.value !== props.options[0].value)) ||
        (props.multiple &&
          Array.isArray(props.value) &&
          (!props.value ||
            props.value.length !== 1 ||
            props.value.indexOf(props.options[0].value) < 0)))
    ) {
      props.onChange(
        props.multiple ? [props.options[0].value] : props.options[0].value
      )
    }
  }, [opcoes])

  const getRightObject = () => (
    <div className={`${rootClassName}-objects`}>
      {props.clearButton &&
      !props.readOnly &&
      ((props.multiple &&
        props.value &&
        Array.isArray(props.value) &&
        props.value.length) ||
        (!props.multiple &&
          (props.value || props.value === 0 || props.value === false))) ? (
        <IconButton
          disabled={props.disabled || false}
          tabIndex={-1}
          className={`${rootClassName}-clear-button`}
          icon={<CloseIcon />}
          onClick={() => props.onChange(props.multiple ? [] : null)}
        />
      ) : undefined}
      <IconButton
        disabled={props.disabled || false}
        tabIndex={-1}
        className={`${rootClassName}-arrow ${rootClassName}-arrow-${
          panelOpened ? 'up' : 'down'
        }`}
        onClick={() => setPanelOpened(!panelOpened)}
        icon={<ChevronArrowDownIcon />}
      />
    </div>
  )

  const getInputProps = () => {
    const p: any = {
      ...props,
      className: getMergedClassNames([rootClassName, props.className]),
      forceFocus: fakeSelectFocused,
      readOnly: true,
      rightObject: getRightObject(),
      onClick: () => {
        const selectFake = document.querySelector(
          `#${id} .${rootClassName}-select-fake`
        ) as HTMLElement
        if (!props.multiple && isMobile() && selectFake) {
          selectFake.focus()
        } else {
          setPanelOpened(!panelOpened)
        }
      },
      onFocus: (evt: any) => {
        const selectFake = document.querySelector(
          `#${id} .${rootClassName}-select-fake`
        ) as HTMLElement
        if (!props.multiple && isMobile() && selectFake) {
          selectFake.focus()
        } else {
          evt.target.setSelectionRange(0, 0)
        }
      },
      onKeyDown: (evt: any) => {
        if (evt.keyCode === 32) {
          // Space
          setPanelOpened(!panelOpened)
          evt.preventDefault()
        }
        if (evt.keyCode === 9 && panelOpened) {
          // Tab
          const inputFilter = document.querySelector(
            `#${panelId} .${rootClassName}-panel-input input`
          ) as HTMLInputElement
          if (inputFilter) {
            inputFilter.focus()
            evt.preventDefault()
          } else {
            const firstOption = document.querySelector(
              `#${id} .${rootClassName}-options div`
            ) as HTMLElement
            if (firstOption) {
              firstOption.focus()
              evt.preventDefault()
            }
          }
        }
      },
      value:
        props.multiple && Array.isArray(props.value)
          ? props.value.length
            ? props.multiplePlaceholder
              ? props.multiplePlaceholder(props.value.length)
              : ''
            : ''
          : props.options
              ?.filter((opt) => opt.value === props.value)
              .map((op) => op.label)[0]
    }
    delete p.showPanelFooter
    delete p.clearButton
    delete p.multiplePlaceholder
    delete p.multiple
    delete p.onChange
    delete p.panelFilter
    delete p.panelFilterPlaceholder
    delete p.options
    delete p.noOptionMessage
    delete p.preSelectUniqueOption
    return p
  }

  const getMultipleOption = (option: any) => {
    const checked =
      Array.isArray(props.value) && props.value.indexOf(option.value) > -1
    const handleChange = () => {
      if (props.readOnly) return
      if (!checked) {
        const val = Array.isArray(props.value) ? props.value.slice() : []
        val.push(option.value)
        props.onChange(val)
      } else {
        const val = Array.isArray(props.value) ? props.value.slice() : []
        val.splice(val.indexOf(option.value), 1)
        props.onChange(val)
      }
    }
    return (
      <div
        tabIndex={1}
        key={getKey(option)}
        className={`${rootClassName}-multiple-option ${
          props.readOnly ? 'read-only' : ''
        }`}
        onKeyDown={(evt) => {
          const child = evt.currentTarget as HTMLElement
          const parent = child.parentNode as HTMLElement
          const index = Array.prototype.indexOf.call(parent.children, child)
          const total = parent.childElementCount
          switch (evt.key) {
            case 'Tab': // Tab
              if (evt.shiftKey) {
                if (index === 0) {
                  const inputFilter = document.querySelector(
                    `#${panelId} .${rootClassName}-panel-input input`
                  ) as HTMLElement
                  if (inputFilter) {
                    inputFilter.focus()
                    evt.preventDefault()
                  }
                }
              } else if (index === total - 1) {
                const selectAll = document.querySelector(
                  `#${panelId} ${rootClassName}-footer-button.${rootClassName}-selectAll`
                ) as HTMLElement
                if (selectAll) {
                  selectAll.focus()
                }
              }
              break
            case 'Enter': // Enter
              if (!checked) {
                handleChange()
              }
              setPanelOpened(false)
              evt.preventDefault()
              break
            case ' ': // Space
              handleChange()
              evt.preventDefault()
              break
            case 'ArrowUp': // Up
              if (index > 0) {
                ;(
                  parent.children[index - 1] as HTMLElement as HTMLElement
                ).focus()
              } else {
                const inputFilter = document.querySelector(
                  `#${panelId} .${rootClassName}-panel-input input`
                ) as HTMLElement
                if (inputFilter) inputFilter.focus()
              }
              evt.preventDefault()
              break
            case 'ArrowDown': // Down
              if (index < total - 1) {
                ;(parent.children[index + 1] as HTMLElement).focus()
                evt.preventDefault()
              }
              break
            default:
              break
          }
        }}
        onClick={handleChange}
      >
        <Checkbox
          value={checked}
          disabled={props.readOnly}
          onChange={() => {
            handleChange()
          }}
        />
        <div className={`${rootClassName}-multiple-option-label`}>
          {option.label}
        </div>
      </div>
    )
  }

  const getSingleOption = (option: any) => {
    const handleChange = () => {
      if (!props.readOnly && props.value !== option.value) {
        setPanelOpened(false)
        props.onChange(option.value)
      }
    }
    return (
      <div
        key={getKey(option)}
        tabIndex={1}
        className={`${rootClassName}-single-option ${
          props.readOnly ? 'read-only' : ''
        }`}
        onClick={handleChange}
        onKeyDown={(evt) => {
          const child = evt.currentTarget as HTMLElement
          const parent = child.parentNode as HTMLElement
          const index = Array.prototype.indexOf.call(parent.children, child)
          const total = parent.childElementCount
          switch (evt.key) {
            case 'Tab': // Tab
              if (evt.shiftKey) {
                if (index === 0) {
                  const inputPanel = document.querySelector(
                    `#${panelId} .${rootClassName}-panel-input input`
                  ) as HTMLElement
                  if (inputPanel) {
                    inputPanel.focus()
                    evt.preventDefault()
                  }
                }
              }
              break
            case 'Enter': // Enter
              handleChange()
              setPanelOpened(false)
              evt.preventDefault()
              break
            case ' ': // Space
              handleChange()
              evt.preventDefault()
              break
            case 'ArrowUp': // Up
              if (index > 0) {
                ;(parent.children[index - 1] as HTMLElement).focus()
              } else {
                const el = document.querySelector(
                  `#${panelId} .${rootClassName}-panel-input input`
                ) as HTMLElement
                if (el) el.focus()
              }
              evt.preventDefault()
              break
            case 'ArrowDown': // Down
              if (index < total - 1) {
                ;(parent.children[index + 1] as HTMLElement).focus()
              }
              evt.preventDefault()
              break
            default:
              break
          }
        }}
      >
        {option.label}
      </div>
    )
  }

  const getNoOptionsMessage = () => {
    return (
      <div className={`${rootClassName}-no-result`}>
        {props.noOptionMessage}
      </div>
    )
  }

  const getOptionsFiltered = () => {
    return props.options?.filter((op) =>
      getStringWithoutSpecialChar(op.label)
        .toLowerCase()
        .match(getStringWithoutSpecialChar(filterInput).toLowerCase())
    )
  }

  return (
    <div id={id} className={`${rootClassName}-outer`}>
      {!props.multiple && isMobile() && (
        <select
          className={`${rootClassName}-select-fake`}
          value={props.value ? String(props.value) : ''}
          onChange={(evt) => props.onChange(evt.target.value)}
          tabIndex={props.tabIndex}
          onFocus={() => setFakeSelectFocused(true)}
          onBlur={() => setFakeSelectFocused(false)}
        >
          <option value='' disabled>
            {languageValues.components.select.single.fakeOption}
          </option>
          {props.options?.map((opt: any) => {
            return (
              <option key={getKey(opt)} value={opt.value}>
                {opt.label}
              </option>
            )
          })}
        </select>
      )}
      <Input {...getInputProps()} />
      <CSSTransition
        in={panelOpened}
        timeout={300}
        classNames='panel-fade'
        unmountOnExit
        onEnter={() => {
          const panel = document.querySelector(`#${panelId}`) as HTMLElement
          if (window.innerHeight < panel.getBoundingClientRect().bottom) {
            panel.style.top = 'unset'
            panel.style.bottom = '0'
          }
          const firstInput = document.querySelector(
            `#${panelId} .${rootClassName}-panel-input input`
          ) as HTMLElement

          if (firstInput && !isMobile()) {
            setTimeout(() => {
              firstInput.focus()
            }, 50)
          } else {
            if (props.multiple) {
              const optionToFocus = document.querySelector(
                `#${panelId} .${rootClassName}-options div`
              ) as HTMLElement
              if (optionToFocus) {
                setTimeout(() => {
                  optionToFocus.focus()
                }, 50)
              }
            } else {
              const index = props.value
                ? props.options
                    ?.map((opt: any, index) => {
                      return { ...opt, ind: index }
                    })
                    .filter((opt) => opt.value === props.value)[0].ind + 1
                : 1
              const query = `#${panelId} .${rootClassName}-options div:nth-child(${index})`
              const optionToFocus = document.querySelector(query) as HTMLElement
              if (optionToFocus) {
                setTimeout(() => {
                  optionToFocus.focus()
                }, 50)
              }
              // if (!props && props !== 0) {
            }
          }
        }}
        onExit={() => {
          const inputElement = document.querySelector(
            `#${id} input`
          ) as HTMLElement
          if (inputElement && !isMobile()) {
            inputElement.focus()
          }
        }}
      >
        <div
          id={panelId}
          className={`${rootClassName}-panel ${
            props.multiple ? 'multiple' : ''
          }`}
        >
          {props.panelFilter && props.options && !!props.options.length && (
            <div className={`${rootClassName}-panel-input`}>
              <Input
                value={filterInput}
                onChange={(evt) => setFilterInput(evt!?.target.value)}
                placeholder={props.panelFilterPlaceholder}
                hideLabelAndHelperText
                onKeyDown={(evt) => {
                  if (
                    (evt.key === 'Tab' && !evt.shiftKey) ||
                    evt.key === 'ArrowDown'
                  ) {
                    const el = document.querySelector(
                      `#${id} .${rootClassName}-options div`
                    ) as HTMLElement
                    if (el) {
                      el.focus()
                      evt.preventDefault()
                    }
                  }
                }}
              />
            </div>
          )}
          <div className={`${rootClassName}-options scroll-white`}>
            {props.options && props.options.length
              ? getOptionsFiltered()?.map((option) => {
                  if (props.multiple) {
                    return getMultipleOption(option)
                  }
                  return getSingleOption(option)
                })
              : getNoOptionsMessage()}
          </div>
          {props.multiple &&
            props.showPanelFooter &&
            props.options &&
            !!props.options.length && (
              <React.Fragment>
                <div className={`${rootClassName}-panel-footer-clearfix`} />
                <div className={`${rootClassName}-panel-footer`}>
                  <Button
                    theme={BUTTON_STYLE_DEFAULT_PRIMARY}
                    disabled={props.readOnly}
                    className={`${rootClassName}-footer-button`}
                    onClick={() =>
                      props.onChange(
                        getOptionsFiltered()?.map((op) => op.value)
                      )
                    }
                    tabIndex={1}
                  >
                    {languageValues.components.select.selectAll}
                  </Button>
                  <Button
                    tabIndex={1}
                    theme={BUTTON_STYLE_DEFAULT_PRIMARY}
                    disabled={props.readOnly}
                    className={`${rootClassName}-footer-button`}
                    onClick={() => props.onChange([])}
                  >
                    {languageValues.components.select.clearAll}
                  </Button>
                </div>
              </React.Fragment>
            )}
        </div>
      </CSSTransition>
    </div>
  )
}

Select.defaultProps = {
  onChange: () => {},
  value: '',
  multiple: false,
  options: [],
  clearButton: true,
  showPanelFooter: true,
  panelFilterPlaceholder:
    languageValues.components.select.panelFilterPlaceholder,
  noOptionMessage: languageValues.components.select.noOptionMessage,
  multiplePlaceholder:
    languageValues.components.select.multiple.numberOfItems.items,
  preSelectUniqueOption: false
}

export default memo(Select)
